import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import Button from "components/button";
import ImageUploadPreview from "components/shared/image-upload-preview";
import Icon from "components/icon";
import Typography from "components/typography";
import Input from "components/input";
import PhoneInput from "components/input/phone-input";
import Select from "components/select";
import { Controller } from "react-hook-form";
import { Table, Columns } from "components/table";
import useEditCustomer, { ExtendedBranchItem, ExtendedAddressItem } from "hooks/customer/useEditCustomer";
import useDebounce from "hooks/shared/useDebounce";

import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmationModal from "components/shared/confirmation-modal";
import {
  useStateServiceV1GetStatesByCountryIdQuery,
} from "_services/modules/master-data/states";
import {
  useCityServiceV1GetCityDropdownQuery,
} from "_services/modules/master-data/cities";
import { useCountryServiceV1ListCountriesQuery } from "_services/modules/master-data/countries";
import {
  addressCategoryOptions,
  getAvailableAddressCategoryOptions,
  getAvailableBranchOptions,
  getOptionLabel,
} from "data/address-category";

export const editcustomerPageRouteName = "/master-data/customer/:id/edit";
export const editCustomerPageRouteName = editcustomerPageRouteName;

// countryOptions will be loaded dynamically from the API inside the component

const EditCustomerPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const {
    t,
    vendorInfoForm,
    vendorBranchForm,
    vendorAddressForm,
    branchItems,
    addressItems,
    branchOptions,
    hasBillingAddress,
    editingBranchIndex,
    editingAddressIndex,
    isLoading,
    isSaving,
    isUploadingLogo,
    isDirty,
    handleUploadLogo,
    handleAddBranch,
    handleEditBranch,
    handleDeleteBranch,
    handleAddAddress,
    handleEditAddress,
    handleDeleteAddress,
    handleSave,
    resultModal,
    handleCloseResultModal,
  } = useEditCustomer();

  const getTranslation = (
    key: string,
    fallback: string,
    options?: Record<string, unknown>,
  ) => {
    // @ts-expect-error temporary i18n key typing limitation
    const val = t(key, options);

    return val === key ? fallback : val;
  };

  const [activeTab, setActiveTab] = useState<"vendor-information" | "branch-list" | "address-list">("vendor-information");

  const { data: countriesData } = useCountryServiceV1ListCountriesQuery({ limit: 1000 });

  const countryOptions = useMemo(() => {
    return (
      countriesData?.countries?.map((c) => ({
        label: c.name ?? "",
        value: c.id ?? "",
      })) ?? []
    );
  }, [countriesData]);

  const selectedCountryId = useMemo(() => {
    const countryVal = vendorAddressForm.watch("country");
    const matched = countryOptions.find((c) => c.label === countryVal || c.value === countryVal);
    return matched?.value ?? (countryVal || "102");
  }, [vendorAddressForm.watch("country"), countryOptions]);

  const selectedProvinceId = vendorAddressForm.watch("province");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const debouncedCitySearchTerm = useDebounce(citySearchTerm, 1000);

  const { data: statesData } = useStateServiceV1GetStatesByCountryIdQuery(
    {
      countryId: selectedCountryId || "102",
      limit: 1000,
    },
  );

  const { data: citiesData, isFetching: isFetchingCityOptions } = useCityServiceV1GetCityDropdownQuery(
    {
      countryId: selectedCountryId,
      stateId: selectedProvinceId || undefined,
      search: debouncedCitySearchTerm || undefined,
    },
    { skip: !selectedCountryId },
  );

  const provinceOptions = useMemo(() => {
    return (
      statesData?.states?.map((state) => ({
        label: state.name ?? "",
        value: state.id ?? "",
      })) ?? []
    );
  }, [statesData]);

  const cityOptions = useMemo(() => {
    return (
      citiesData?.cities?.map((city) => ({
        label: city.name ?? "",
        value: city.id ?? "",
      })) ?? []
    );
  }, [citiesData]);

  const handleSearchCity = useCallback(
    async (term: string) => {
      setCitySearchTerm(term);
      return cityOptions;
    },
    [cityOptions],
  );

  const availableCategoryOptions = useMemo(
    () => getAvailableAddressCategoryOptions(addressItems, editingAddressIndex),
    [addressItems, editingAddressIndex],
  );

  const availableBranchOptions = useMemo(
    () => getAvailableBranchOptions(branchItems, addressItems, editingAddressIndex),
    [branchItems, addressItems, editingAddressIndex],
  );

  const {
    register: registerInfo,
    watch: watchInfo,
    formState: { errors: errorsInfo },
    setValue: setValueInfo,
  } = vendorInfoForm;

  const vendorType = watchInfo("vendorType");
  const currentLogo = watchInfo("vendorLogo");

  // Logo upload state
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);

  const handleLogoUpload = async (file: File) => {
    const validTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      toast.error(getTranslation("error-file-invalid-format", "Invalid logo format. Use JPG or PNG.", { formats: ".jpg, .jpeg, .png" }));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error(getTranslation("error-file-max-size", "Logo size exceeds 2MB.", { size: 2 }));
      return;
    }

    const localPreview = URL.createObjectURL(file);
    setLogoPreviewUrl(localPreview);

    try {
      const uploadedUrl = await handleUploadLogo(file);
      if (uploadedUrl) {
        setLogoPreviewUrl(uploadedUrl);
      }
    } catch {
      setLogoPreviewUrl(currentLogo || null);
    }
  };

  const handleLogoRemove = () => {
    setLogoPreviewUrl(null);
    vendorInfoForm.setValue("vendorLogo", "", { shouldDirty: true });
    vendorInfoForm.setValue("vendorLogoFile", null, { shouldDirty: true });
  };

  const logoDisplayUrl = logoPreviewUrl || currentLogo;

  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  useEffect(() => {
    const handleGlobalBack = (e: Event) => {
      if (isDirty) {
        e.preventDefault();
        setShowUnsavedModal(true);
      }
    };
    window.addEventListener("global-back-button-click", handleGlobalBack);
    return () => window.removeEventListener("global-back-button-click", handleGlobalBack);
  }, [isDirty]);

  const handleCancelClick = () => {
    if (isDirty) {
      setShowUnsavedModal(true);
    } else {
      navigate(`/master-data/customer/${id}/detail`);
    }
  };

  const handleDiscard = useCallback(() => {
    setShowUnsavedModal(false);
    navigate(`/master-data/customer/${id}/detail`);
  }, [navigate, id]);

  const handleSaveClick = async () => {
    const isValidVendorInfo = await vendorInfoForm.trigger();
    if (!isValidVendorInfo) {
      setActiveTab("vendor-information");
      toast.error(t("toast-complete-required-fields") || "Please complete all required fields");
      return;
    }

    if (branchItems.length === 0) {
      setActiveTab("branch-list");
      toast.error(t("toast-branch-required") || "At least one branch is required");
      return;
    }

    if (addressItems.length === 0) {
      setActiveTab("address-list");
      toast.error(t("toast-address-required") || "At least one address is required");
      return;
    }

    const hasAddressForEveryBranch = branchItems.every((branch) =>
      addressItems.some((address) => address.branch === branch.branchName),
    );

    if (!hasAddressForEveryBranch) {
      setActiveTab("address-list");
      toast.error(t("toast-all-branches-must-have-address") || "All branches must have at least one address");
      return;
    }

    if (!hasBillingAddress) {
      setActiveTab("address-list");
      toast.error(t("toast-billing-required") || "One billing address is required");
      return;
    }

    setShowSaveModal(true);
  };

  const handleConfirmSave = useCallback(async () => {
    setShowSaveModal(false);
    await handleSave();
  }, [handleSave]);

  const branchColumns: Columns<ExtendedBranchItem>[] = useMemo(
    () => [
      {
        id: "no",
        fieldId: "index",
        label: t("col-no") || "No.",
        dragDisabled: true,
        width: "50px",
      },
      {
        id: "branch-name",
        fieldId: "branchName",
        label: t("col-branch-name") || "Branch Name",
        dragDisabled: true,
      },
      {
        id: "pic-name",
        fieldId: "picName",
        label: t("col-pic-name") || "PIC Name",
        dragDisabled: true,
      },
      {
        id: "pic-email",
        fieldId: "picEmail",
        label: t("col-pic-email") || "PIC Email",
        dragDisabled: true,
      },
      {
        id: "pic-phone",
        fieldId: "picPhoneNumber",
        label: t("col-pic-phone") || "PIC Phone Number",
        render: (row) => `${row.picCountryCode || "+62"} ${row.picPhoneNumber || ""}`,
        dragDisabled: true,
      },
      {
        id: "action",
        fieldId: "branchName",
        label: t("col-action") || "Action",
        dragDisabled: true,
        render: (row) => {
          const idx = branchItems.indexOf(row);
          return (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                color="tertiary"
                size="small"
                iconLeft={<Icon name="edit-2" size={14} />}
                onClick={() => handleEditBranch(idx)}
              />
              <Button
                type="button"
                variant="outline"
                color="error"
                size="small"
                iconLeft={<Icon name="trash" size={14} />}
                onClick={() => handleDeleteBranch(idx)}
              />
            </div>
          );
        },
      },
    ],
    [t, branchItems, handleEditBranch, handleDeleteBranch]
  );

  const addressColumns: Columns<ExtendedAddressItem>[] = useMemo(
    () => [
      {
        id: "no",
        fieldId: "index",
        label: t("col-no") || "No.",
        dragDisabled: true,
        width: "50px",
      },
      {
        id: "addressName",
        fieldId: "addressName",
        label: t("col-address") || "Address",
        dragDisabled: true,
        render: (row) => (
          <div>
            <div className="font-semibold text-text-primary">{row.addressName}</div>
            {row.isBillingAddress ? (
              <span className="inline-flex items-center rounded bg-greyScale-95 px-1.5 py-0.5 text-[10px] font-semibold text-greyScale-60 border border-greyScale-90 mt-1">
                {t("billing-address-tag") || "Billing Address"}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        id: "addressCategory",
        fieldId: "addressCategory",
        label: t("col-category") || "Category",
        dragDisabled: true,
        render: (row) => getOptionLabel(addressCategoryOptions, row.addressCategory ?? ""),
      },
      {
        id: "branch",
        fieldId: "branch",
        label: t("col-branch") || "Branch",
        dragDisabled: true,
      },
      {
        id: "fullAddress",
        fieldId: "fullAddress",
        label: t("col-full-address") || "Full Address",
        dragDisabled: true,
        render: (row) => <div className="max-w-[280px] truncate">{row.fullAddress}</div>,
      },
      {
        id: "country",
        fieldId: "country",
        label: t("col-country") || "Country",
        dragDisabled: true,
        render: (row) => {
          const matched = countryOptions.find((c) => c.value === row.country || c.label === row.country);
          return matched?.label || row.countryName || (row.country === "102" ? "Indonesia" : row.country) || "Indonesia";
        },
      },
      {
        id: "province",
        fieldId: "province",
        label: t("col-province") || "Province",
        dragDisabled: true,
        render: (row) => {
          const matched = provinceOptions.find((p) => p.value === row.province || p.label === row.province);
          return row.provinceName || matched?.label || row.province || "—";
        },
      },
      {
        id: "city",
        fieldId: "city",
        label: t("col-city") || "City",
        dragDisabled: true,
        render: (row) => {
          const matched = cityOptions.find((c) => c.value === row.city || c.label === row.city);
          return row.cityName || matched?.label || row.city || "—";
        },
      },
      {
        id: "action",
        fieldId: "addressName",
        label: t("col-action") || "Action",
        dragDisabled: true,
        render: (row) => {
          const idx = addressItems.indexOf(row);
          return (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                color="tertiary"
                size="small"
                iconLeft={<Icon name="edit-2" size={14} />}
                onClick={() => handleEditAddress(idx)}
              />
              <Button
                type="button"
                variant="outline"
                color="error"
                size="small"
                iconLeft={<Icon name="trash" size={14} />}
                onClick={() => handleDeleteAddress(idx)}
              />
            </div>
          );
        },
      },
    ],
    [t, addressItems, countryOptions, provinceOptions, cityOptions, handleEditAddress, handleDeleteAddress]
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Typography variant="body" tone="muted">
          {/* @ts-expect-error temporary i18n key typing limitation */}
          {t("loading") || "Loading..."}
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-2xl border border-border bg-white shadow-sm">
        {/* Tab Headers */}
        <div className="flex items-center justify-between border-b border-border px-6">
          <div className="flex gap-8">
            <button
              type="button"
              onClick={() => setActiveTab("vendor-information")}
              className={`relative py-4 text-sm font-semibold transition-all ${activeTab === "vendor-information"
                ? "text-primary-600"
                : "text-greyScale-60 hover:text-text-primary"
                }`}
            >
              <div className="flex items-center gap-2">
                <Icon name="profile-2user" size={18} />
                <span>Customer Information</span>
              </div>
              {activeTab === "vendor-information" && (
                <span className="absolute bottom-0 left-0 h-0.5 w-full bg-primary-600" />
              )}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("branch-list")}
              className={`relative py-4 text-sm font-semibold transition-all ${activeTab === "branch-list"
                ? "text-primary-600"
                : "text-greyScale-60 hover:text-text-primary"
                }`}
            >
              <div className="flex items-center gap-2">
                <Icon name="building" size={18} />
                <span>Branch List</span>
              </div>
              {activeTab === "branch-list" && (
                <span className="absolute bottom-0 left-0 h-0.5 w-full bg-primary-600" />
              )}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("address-list")}
              className={`relative py-4 text-sm font-semibold transition-all ${activeTab === "address-list"
                ? "text-primary-600"
                : "text-greyScale-60 hover:text-text-primary"
                }`}
            >
              <div className="flex items-center gap-2">
                <Icon name="location" size={18} />
                <span>Address List</span>
              </div>
              {activeTab === "address-list" && (
                <span className="absolute bottom-0 left-0 h-0.5 w-full bg-primary-600" />
              )}
            </button>
          </div>
        </div>

        {/* Tab Contents */}
        <div className="p-6">
          {activeTab === "vendor-information" && (
            <div className="flex flex-col gap-6">
              <div>
                <Typography variant="bodyLarge" className="mb-2 font-semibold">
                  {t("label-vendor-type") || "Customer Type"} <span className="text-error-500">*</span>
                </Typography>
                <Controller
                  name="vendorType"
                  control={vendorInfoForm.control}
                  render={({ field }) => (
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        className={`h-[42px] rounded-xl border px-4 text-left text-sm font-semibold transition-colors ${field.value === "individual"
                          ? "border-primary-500 bg-primary-50 text-primary-700"
                          : "border-border hover:bg-greyScale-95"
                          }`}
                        onClick={() => field.onChange("individual")}
                      >
                        {t("vendor-type-individual") || "Individual"}
                      </button>
                      <button
                        type="button"
                        className={`h-[42px] rounded-xl border px-4 text-left text-sm font-semibold transition-colors ${field.value === "company"
                          ? "border-primary-500 bg-primary-50 text-primary-700"
                          : "border-border hover:bg-greyScale-95"
                          }`}
                        onClick={() => field.onChange("company")}
                      >
                        {t("vendor-type-company") || "Company"}
                      </button>
                    </div>
                  )}
                />
              </div>

              <div className="grid grid-cols-[180px_1fr] gap-6">
                <div className="flex flex-col gap-2">
                  <Typography variant="bodyLarge" className="font-semibold">{t("label-vendor-logo") || "Customer Logo"}</Typography>
                  <ImageUploadPreview
                    id="customer-logo-upload-edit"
                    dataTestId="upload-customer-logo-edit"
                    value={logoDisplayUrl}
                    fileName={vendorInfoForm.watch("vendorLogoFile")?.name}
                    onUpload={handleLogoUpload}
                    onRemove={handleLogoRemove}
                    isUploading={isUploadingLogo}
                    dropText={t("logo-drop-text") || "Click to upload"}
                    helperText={t("logo-drop-helper") || "Format: JPG, PNG (Max. 2MB)"}
                    accept="image/jpeg,image/png,image/jpg"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label={t("label-vendor-name") || "Customer Name"}
                    required
                    placeholder={t("placeholder-vendor-name") || "Enter customer name"}
                    {...registerInfo("vendorName")}
                    error={errorsInfo.vendorName}
                  />
                  <Input
                    label={t("label-vendor-id") || "Customer ID"}
                    required
                    placeholder={t("placeholder-vendor-id") || "Enter customer ID"}
                    {...registerInfo("vendorId")}
                    error={errorsInfo.vendorId}
                    disabled={true}
                  />
                  <Input
                    label={t("label-vendor-email") || "Customer Email"}
                    required
                    placeholder={t("placeholder-vendor-email") || "Enter customer email"}
                    {...registerInfo("email")}
                    error={errorsInfo.email}
                  />

                  <Input
                    label={t("label-contact-person-name") || "Contact Person Name"}
                    required
                    placeholder={t("placeholder-contact-person-name") || "Enter CP name"}
                    {...registerInfo("contactPersonName")}
                    error={errorsInfo.contactPersonName}
                  />
                  <Controller
                    name="contactPersonPhoneNumber"
                    control={vendorInfoForm.control}
                    render={({ field, fieldState }) => (
                      <PhoneInput
                        label={t("label-contact-person-phone") || "Contact Person Phone Number"}
                        required
                        value={field.value}
                        onChange={(event) => field.onChange(event.target.value)}
                        countryCode={watchInfo("contactPersonPhoneCountryCode")}
                        onCountryChange={(code) => setValueInfo("contactPersonPhoneCountryCode", code)}
                        placeholder={t("placeholder-contact-person-phone") || "Enter CP phone"}
                        error={fieldState.error}
                      />
                    )}
                  />

                  {vendorType === "company" && (
                    <Input
                      label={t("label-legal-name") || "Legal Name"}
                      required
                      placeholder={t("placeholder-legal-name") || "Enter legal name"}
                      {...registerInfo("legalName")}
                      error={errorsInfo.legalName}
                    />
                  )}

                  <Controller
                    name="status"
                    control={vendorInfoForm.control}
                    render={({ field, fieldState }) => (
                      <Select
                        label={t("label-status") || "Status"}
                        required
                        options={[
                          { label: t("status-active") || "Active", value: "active" },
                          { label: t("status-inactive") || "Inactive", value: "inactive" },
                        ]}
                        value={field.value}
                        onValueChange={(value) => field.onChange(value)}
                        error={fieldState.error}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t border-border">
                <Button type="button" variant="fill" color="primary" onClick={() => setActiveTab("branch-list")}>
                  {t("btn-next") || "Next"}
                </Button>
              </div>
            </div>
          )}

          {activeTab === "branch-list" && (
            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-2 gap-4 p-5 rounded-2xl border border-border bg-greyScale-98">
                <Input
                  label={t("label-branch-name") || "Branch Name"}
                  required
                  placeholder={t("placeholder-branch-name") || "Enter branch name"}
                  {...vendorBranchForm.register("branchName")}
                  error={vendorBranchForm.formState.errors.branchName}
                />
                <Input
                  label={t("label-pic-name") || "PIC Name"}
                  required
                  placeholder={t("placeholder-pic-name") || "Enter PIC name"}
                  {...vendorBranchForm.register("picName")}
                  error={vendorBranchForm.formState.errors.picName}
                />

                <Input
                  label={t("label-pic-email") || "PIC Email"}
                  required
                  placeholder={t("placeholder-pic-email") || "Enter PIC email"}
                  {...vendorBranchForm.register("picEmail")}
                  error={vendorBranchForm.formState.errors.picEmail}
                />
                <Controller
                  name="picPhoneNumber"
                  control={vendorBranchForm.control}
                  render={({ field, fieldState }) => (
                    <PhoneInput
                      label={t("label-pic-phone-number") || "PIC Phone Number"}
                      required
                      value={field.value}
                      onChange={(event) => field.onChange(event.target.value)}
                      countryCode={vendorBranchForm.watch("picCountryCode")}
                      onCountryChange={(code) => vendorBranchForm.setValue("picCountryCode", code)}
                      placeholder={t("placeholder-pic-phone-number") || "Enter PIC phone"}
                      error={fieldState.error}
                    />
                  )}
                />

                <div className="col-span-2 flex justify-end gap-2">
                  {editingBranchIndex !== null && (
                    <Button
                      type="button"
                      variant="outline"
                      color="tertiary"
                      onClick={() => {
                        vendorBranchForm.reset({
                          branchName: "",
                          picName: "",
                          picEmail: "",
                          picCountryCode: "+62",
                          picPhoneNumber: "",
                        });
                        handleAddBranch();
                      }}
                    >
                      Cancel Edit
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="fill"
                    color="primary"
                    onClick={handleAddBranch}
                  >
                    {editingBranchIndex !== null ? (t("btn-save-changes") || "Save Changes") : (t("btn-add-to-list") || "Add to List")}
                  </Button>
                </div>
              </div>

              <div className="rounded-2xl border border-border overflow-hidden">
                <Table<ExtendedBranchItem>
                  columns={branchColumns}
                  data={branchItems}
                  rowKey="branchName"
                  currentPage={1}
                  limit={100}
                />
              </div>

              <div className="flex justify-between pt-4 border-t border-border">
                <Button type="button" variant="outline" color="tertiary" onClick={() => setActiveTab("vendor-information")}>
                  Back
                </Button>
                <Button type="button" variant="fill" color="primary" onClick={() => setActiveTab("address-list")}>
                  {t("btn-next") || "Next"}
                </Button>
              </div>
            </div>
          )}

          {activeTab === "address-list" && (
            <div className="flex flex-col gap-6">
              {!hasBillingAddress && (
                <div className="rounded-xl bg-error-100 border border-error-200 px-4 py-3 text-error-700 text-sm flex items-center gap-2">
                  <Icon name="info" size={16} />
                  <span>{t("billing-required-alert") || "One billing address is required."}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 p-5 rounded-2xl border border-border bg-greyScale-98">
                <Input
                  label={t("label-address-name") || "Address Name"}
                  required
                  placeholder={t("placeholder-address-name") || "Enter address name"}
                  {...vendorAddressForm.register("addressName")}
                  error={vendorAddressForm.formState.errors.addressName}
                />

                <Controller
                  name="addressCategory"
                  control={vendorAddressForm.control}
                  render={({ field, fieldState }) => (
                    <Select
                      label={t("label-address-category") || "Address Category"}
                      required
                      options={availableCategoryOptions}
                      value={field.value}
                      onValueChange={(value) => field.onChange(value)}
                      error={fieldState.error}
                      placeholder={t("placeholder-select") || "Select category"}
                    />
                  )}
                />

                <Controller
                  name="branch"
                  control={vendorAddressForm.control}
                  render={({ field, fieldState }) => (
                    <Select
                      label={t("label-branch") || "Branch"}
                      required
                      options={availableBranchOptions}
                      value={field.value}
                      onValueChange={(value) => field.onChange(value)}
                      error={fieldState.error}
                      placeholder={t("placeholder-select") || "Select branch"}
                    />
                  )}
                />

                <Controller
                  name="isBillingAddress"
                  control={vendorAddressForm.control}
                  render={({ field }) => (
                    <label className="flex items-center justify-between px-4 py-3 rounded-xl border border-border bg-white cursor-pointer select-none">
                      <div>
                        <Typography variant="body" className="font-semibold text-text-primary">
                          {t("label-billing-address") || "Billing Address"}
                        </Typography>
                        <Typography variant="bodySmall" tone="muted">
                          {t("label-billing-address-helper") || "Set as target for invoice generation"}
                        </Typography>
                      </div>
                      <button
                        type="button"
                        className={`w-11 h-6 rounded-full p-1 transition-colors outline-none focus:ring-2 focus:ring-primary-500/20 ${field.value ? "bg-primary-500" : "bg-greyScale-200"
                          }`}
                        onClick={() => field.onChange(!field.value)}
                      >
                        <span
                          className={`block w-4 h-4 rounded-full bg-white transition-transform ${field.value ? "translate-x-5" : "translate-x-0"
                            }`}
                        />
                      </button>
                    </label>
                  )}
                />

                <Controller
                  name="country"
                  control={vendorAddressForm.control}
                  render={({ field, fieldState }) => {
                    const matched = countryOptions.find((c) => c.label === field.value || c.value === field.value);
                    const selectValue = matched?.value ?? field.value;

                    return (
                      <Select
                        label={t("label-country") || "Country"}
                        required
                        searchable
                        options={countryOptions}
                        value={selectValue}
                        onValueChange={(value) => {
                          field.onChange(value);
                          const selectedCountry = countryOptions.find((c) => c.value === value || c.label === value);
                          vendorAddressForm.setValue("countryName", selectedCountry?.label || (value === "102" ? "Indonesia" : (value as string)));
                          vendorAddressForm.setValue("province", "");
                          vendorAddressForm.setValue("provinceName", "");
                          vendorAddressForm.setValue("city", "");
                          vendorAddressForm.setValue("cityName", "");
                        }}
                        error={fieldState.error}
                        placeholder={t("placeholder-select") || "Select country"}
                      />
                    );
                  }}
                />

                <Controller
                  name="province"
                  control={vendorAddressForm.control}
                  render={({ field, fieldState }) => (
                    <Select
                      label={t("label-province") || "Province"}
                      required
                      searchable
                      disabled={!selectedCountryId}
                      options={provinceOptions}
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        const selectedProvince = provinceOptions.find((p) => p.value === value || p.label === value);
                        vendorAddressForm.setValue("provinceName", selectedProvince?.label || "");
                        vendorAddressForm.setValue("city", "");
                        vendorAddressForm.setValue("cityName", "");
                      }}
                      error={fieldState.error}
                      placeholder={t("placeholder-select") || "Select province"}
                    />
                  )}
                />

                <Controller
                  name="city"
                  control={vendorAddressForm.control}
                  render={({ field, fieldState }) => (
                    <Select
                      label={t("label-city") || "City"}
                      required
                      searchable
                      loading={isFetchingCityOptions}
                      disabled={!selectedCountryId}
                      data-testid="select-branch-city"
                      options={cityOptions}
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        const selectedCity = cityOptions.find((c) => c.value === value || c.label === value);
                        vendorAddressForm.setValue("cityName", selectedCity?.label || "");
                      }}
                      onSearch={handleSearchCity}
                      error={fieldState.error}
                      placeholder={t("placeholder-select") || "Select city"}
                    />
                  )}
                />

                <Input
                  label={t("label-postal-code") || "Postal Code"}
                  required
                  placeholder={t("placeholder-postal-code") || "Enter postal code"}
                  {...vendorAddressForm.register("postalCode")}
                  error={vendorAddressForm.formState.errors.postalCode}
                />

                <Input
                  label={t("label-full-address") || "Full Address"}
                  required
                  placeholder={t("placeholder-full-address") || "Enter full address"}
                  {...vendorAddressForm.register("fullAddress")}
                  error={vendorAddressForm.formState.errors.fullAddress}
                />

                <Input
                  label={t("label-google-maps-url") || "Google Maps URL"}
                  required
                  placeholder={t("placeholder-google-maps-url") || "Enter Maps URL"}
                  {...vendorAddressForm.register("mapsUrl")}
                  error={vendorAddressForm.formState.errors.mapsUrl}
                />

                <div className="col-span-2 flex justify-end gap-2">
                  {editingAddressIndex !== null && (
                    <Button
                      type="button"
                      variant="outline"
                      color="tertiary"
                      onClick={() => {
                        vendorAddressForm.reset({
                          addressName: "",
                          addressCategory: "branch",
                          branch: "",
                          country: "Indonesia",
                          province: "",
                          city: "",
                          postalCode: "",
                          fullAddress: "",
                          mapsUrl: "",
                          isBillingAddress: false,
                        });
                        handleAddAddress();
                      }}
                    >
                      Cancel Edit
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="fill"
                    color="primary"
                    onClick={handleAddAddress}
                  >
                    {editingAddressIndex !== null ? (t("btn-save-changes") || "Save Changes") : (t("btn-add-to-list") || "Add to List")}
                  </Button>
                </div>
              </div>

              <div className="rounded-2xl border border-border overflow-hidden">
                <Table<ExtendedAddressItem>
                  columns={addressColumns}
                  data={addressItems}
                  rowKey="addressName"
                  currentPage={1}
                  limit={100}
                />
              </div>

              <div className="flex justify-between pt-4 border-t border-border">
                <Button type="button" variant="outline" color="tertiary" onClick={() => setActiveTab("branch-list")}>
                  Back
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Sticky Bottom Actions Bar */}
      <div className="sticky bottom-0 bg-white border-t border-border px-6 py-4 flex justify-end gap-3 z-30 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          size="medium"
          onClick={handleCancelClick}
          disabled={isSaving}
          data-testid="btn-cancel-vendor"
        >
          {t("btn-cancel") || "Cancel"}
        </Button>
        <Button
          type="button"
          variant="fill"
          color="primary"
          size="medium"
          onClick={handleSaveClick}
          disabled={isSaving}
          data-testid="btn-save-vendor"
        >
          {isSaving ? "Saving..." : (t("btn-save") || "Save")}
        </Button>
      </div>

      <ConfirmationModal
        open={showUnsavedModal}
        title={getTranslation("modal-unsaved-title", "You have unsaved changes")}
        message={getTranslation("modal-unsaved-message", "This action cannot be undone. Save as draft or discard before leaving.")}
        cancelLabel={getTranslation("btn-cancel", "Cancel")}
        confirmLabel={getTranslation("btn-discard-changes", "Discard Changes")}
        onCancel={() => setShowUnsavedModal(false)}
        onConfirm={handleDiscard}
        icon="info"
      />

      <ConfirmationModal
        open={showSaveModal}
        title={getTranslation("modal-save-title", "Save this data?")}
        message={getTranslation("modal-save-message", "Are you sure you want to save this information?")}
        cancelLabel={getTranslation("btn-cancel", "Cancel")}
        confirmLabel={getTranslation("btn-save", "Save")}
        onCancel={() => setShowSaveModal(false)}
        onConfirm={handleConfirmSave}
        icon="info"
        loading={isSaving}
      />

      <ConfirmationModal
        open={resultModal.open}
        title={resultModal.title}
        message={resultModal.message}
        confirmLabel={getTranslation("btn-close", "Close")}
        hideCancelButton
        onConfirm={handleCloseResultModal}
        onCancel={handleCloseResultModal}
        icon={resultModal.icon}
      />
    </div>
  );
};

export default EditCustomerPage;
